import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getShopDetails } from "./api";
import { useCart } from "./context/CartContext";
import ShopNavBar from "./components/ShopNavBar";
import Spinner from "./components/Spinner";
import EmptyState from "./components/EmptyState";

export default function ProductDetail() {
  const { shopId, productId } = useParams();
  const navigate = useNavigate();
  const { addItem, itemCount } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    getShopDetails(shopId, { limit: 100 })
      .then((res) => {
        const found = (res.data.products || []).find(
          (p) => p.id === productId,
        );
        if (!found) {
          setError("Product not found.");
          return;
        }
        setProduct(found);
        if (found.variants?.length > 0) setSelectedVariant(found.variants[0]);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [shopId, productId]);

  const handleAddToCart = () => {
    if (product.variants?.length > 0 && !selectedVariant) return;
    addItem(product, selectedVariant, quantity);
    setAdded(true);
    setTimeout(() => navigate(`/shop/${shopId}/cart`), 450);
  };

  const navBar = (
    <ShopNavBar
      title={!loading && product ? product.title : "Product"}
      onCart={() => navigate(`/shop/${shopId}/cart`)}
      cartCount={itemCount}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen">
        {navBar}
        <Spinner label="Loading product…" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        {navBar}
        <EmptyState
          title="Product not found"
          subtitle={error || "This item may no longer be available."}
        />
      </div>
    );
  }

  const displayPrice = selectedVariant?.price ?? product.price;
  const outOfStock =
    selectedVariant && selectedVariant.stock != null && selectedVariant.stock <= 0;

  return (
    <div className="min-h-screen pb-28">
      {navBar}

      <div className="w-full aspect-square bg-gray-100">
        {product.image && (
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div>
          <h1 className="text-lg font-bold otaDark leading-snug">
            {product.title}
          </h1>
          <p className="text-xl font-bold otaFont mt-1">
            ₹{displayPrice.toFixed(0)}
          </p>
        </div>

        {product.description && (
          <p className="text-sm otaLight leading-relaxed">
            {product.description}
          </p>
        )}

        {product.variants?.length > 0 && (
          <div>
            <p className="text-sm font-medium otaDark mb-2">Choose an option</p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => {
                const soldOut = v.stock != null && v.stock <= 0;
                return (
                  <button
                    key={v.id}
                    onClick={() => !soldOut && setSelectedVariant(v)}
                    disabled={soldOut}
                    className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      selectedVariant?.id === v.id
                        ? "border-transparent otaGradientButton text-white"
                        : soldOut
                          ? "border-gray-200 text-gray-300 line-through"
                          : "border-gray-300 otaDark active:bg-gray-50"
                    }`}
                  >
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4">
          <p className="text-sm font-medium otaDark">Quantity</p>
          <div className="flex items-center gap-3 border border-gray-300 rounded-lg px-2 py-1">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-7 h-7 text-lg font-semibold otaDark active:bg-gray-100 rounded-md transition-colors"
            >
              −
            </button>
            <span className="text-sm font-semibold w-4 text-center">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-7 h-7 text-lg font-semibold otaDark active:bg-gray-100 rounded-md transition-colors"
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] p-4 bg-white border-t border-gray-100">
        <button
          onClick={handleAddToCart}
          disabled={outOfStock}
          className={`w-full font-semibold rounded-xl py-3.5 transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 ${
            added ? "bg-green-500 text-white" : "otaGradientButton text-white"
          }`}
        >
          {outOfStock ? "Out of stock" : added ? "Added to cart ✓" : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}
